import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Heart, Shield, HandHeart, Users, ArrowRight, Check, Calendar, MessageSquare, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import usePageMeta from "../lib/usePageMeta";

const TALK_ABOUT = [
  "Grief or the death of someone you love",
  "Marriage, family, or relationship difficulties",
  "Stress or emotional challenges",
  "Financial hardship",
  "Illness or health concerns",
  "Caregiving responsibilities",
  "Major life changes",
  "Workplace or personal concerns",
  "Spiritual questions, when you choose to discuss them",
  "Or simply when you need someone to listen",
];

const PRINCIPLES = [
  {
    icon: HandHeart,
    title: "Voluntary",
    body: "Speaking with a chaplain is always your choice. You are never required to participate in chaplain care or discuss anything you do not wish to discuss.",
  },
  {
    icon: Users,
    title: "Permission-based",
    body: "Your boundaries matter. Chaplain care is centered on respectful, permission-based conversations. You determine when you would like support and what you are comfortable discussing.",
  },
  {
    icon: Shield,
    title: "Private and confidential",
    body: "Your privacy is important. Chaplain conversations are treated as confidential within the boundaries of applicable law, organizational policy, and situations involving immediate safety concerns. Your chaplain can explain these confidentiality boundaries before you share sensitive information.",
  },
  {
    icon: Heart,
    title: "Respectful of every person",
    body: "Chaplain care is available regardless of your faith, beliefs, background, or whether you practice a religion at all. Our role is not to pressure you toward a particular belief. Our role is to care for the person in front of us.",
  },
];

const NOT_LIST = [
  "Supervisor",
  "Manager",
  "Human Resources representative",
  "Therapist or medical provider",
  "Disciplinary authority",
  "Religious recruiter",
];

/**
 * "Welcome to Chaplain Care" — dedicated Start Here page at /care-portal/start-here.
 * Wrapped by the site Layout via the App.js route. Introduces employees to
 * chaplain care and ends with both "Request Confidential Support" and
 * "Schedule a Chaplain" calls to action.
 */
export default function WelcomeToChaplainCare() {
  usePageMeta({
    title: "Start Here — Chaplain Care | CCCHAPS",
    description: "Learn what a chaplain does, how chaplain care works, what you can talk about, and what to expect when you reach out.",
  });
  const [scheduleUrl, setScheduleUrl] = useState("");
  useEffect(() => {
    api.get("/config").then((r) => setScheduleUrl(r.data?.schedule_chaplain_url || "")).catch(() => {});
  }, []);
  const scheduleTarget = scheduleUrl || "/care-portal/request-support";
  const scheduleExternal = Boolean(scheduleUrl);

  return (
    <div data-testid="start-here-page" className="bg-white">
      <section
        data-testid="welcome-to-chaplain-care"
        className="relative py-16 md:py-24"
        aria-labelledby="welcome-heading"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/care-portal" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#4A6FA5] mb-6" data-testid="start-here-back">
            <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back to Care Portal
          </Link>
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">Start here</p>
            <h1 id="welcome-heading" className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 mt-3 text-balance">
              Welcome to Chaplain Care
            </h1>
            <p className="mt-3 text-lg sm:text-xl text-[#4A6FA5] font-heading">
              Support for life's difficult moments.
            </p>
            <p className="mt-5 text-slate-700 leading-relaxed">
              Life doesn't stop when the workday begins. Personal challenges, family concerns, loss, stress, and unexpected circumstances can affect any of us.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              CCCHAPS provides compassionate chaplain care so you have someone available to listen, encourage, and support you when you choose to reach out.
            </p>
          </div>

          {/* Warm human photograph */}
          <figure
            data-testid="start-here-welcome-image"
            className="mt-10 rounded-2xl overflow-hidden border border-slate-200 shadow-sm"
          >
            <div className="aspect-[16/9] w-full bg-slate-100">
              <img
                src="/images/care-portal-start-here/welcome.jpg"
                alt="Two people having a private supportive conversation at a table"
                loading="lazy"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 35%" }}
              />
            </div>
          </figure>

        {/* What is a corporate chaplain */}
        <div className="mt-12 grid lg:grid-cols-2 gap-8 lg:gap-14 items-start">
          <div>
            <h3 className="font-heading text-2xl text-slate-900">What is a corporate chaplain?</h3>
            <p className="mt-3 text-slate-700 leading-relaxed">
              A corporate chaplain is a trained caregiver who provides personal and compassionate support to people in the workplace.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              Your chaplain is there to listen without judgment, offer encouragement, and help you navigate difficult moments. Chaplain care is available to people of all backgrounds, beliefs, and life experiences.
            </p>
            <p className="mt-4 text-sm text-[#4A6FA5] font-semibold">
              You do not have to be religious to speak with a chaplain.
            </p>
          </div>

          {/* When can I talk with a chaplain */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 shadow-sm">
            <h3 className="font-heading text-xl text-slate-900">When can I talk with a chaplain?</h3>
            <p className="mt-2 text-sm text-[#4A6FA5] font-semibold">
              CCCHAPS chaplains are available 24/7 on-call — by phone, text, available remote support, or a confidential message.
            </p>
            <p className="mt-2 text-sm text-slate-600">You may choose to reach out when dealing with situations such as:</p>
            <ul className="mt-4 space-y-2">
              {TALK_ABOUT.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 mt-0.5 text-[#4A6FA5] shrink-0" strokeWidth={2.5} aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm font-semibold text-slate-900">You decide what you want to share.</p>
          </div>
        </div>

        {/* How chaplain care works */}
        <div className="mt-16">
          <h3 className="font-heading text-2xl text-slate-900">How chaplain care works</h3>
          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            {PRINCIPLES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`principle-${title.split(" ")[0].toLowerCase()}`}>
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F0F4F8] text-[#4A6FA5] mb-3">
                  <Icon className="w-5 h-5" strokeWidth={1.75} aria-hidden="true" />
                </div>
                <p className="font-heading text-lg text-slate-900">{title}</p>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* What a chaplain is NOT */}
        <div className="mt-16 rounded-2xl bg-white border border-slate-200 p-6 md:p-8">
          <h3 className="font-heading text-2xl text-slate-900">What a CCCHAPS chaplain is not</h3>
          <p className="mt-2 text-slate-700">Your chaplain is not your:</p>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-700">
            {NOT_LIST.map((item) => (
              <li key={item} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-300" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm text-slate-600 leading-relaxed">
            Chaplains provide a distinct form of compassionate care and can help connect you with appropriate professional or community resources when your needs extend beyond chaplain care.
          </p>
        </div>

        {/* Closing + CTAs */}
        <div className="mt-16 text-center max-w-2xl mx-auto">
          <h3 className="font-heading text-2xl sm:text-3xl text-slate-900">You don't have to face everything alone.</h3>
          <p className="mt-4 text-slate-700 leading-relaxed">
            Sometimes people need advice. Sometimes they need resources. And sometimes they simply need someone who will listen.
          </p>
          <p className="mt-3 text-slate-700 leading-relaxed">
            Whatever brings you to CCCHAPS, you are welcome to reach out when you need support.
          </p>
          <p className="mt-6 font-heading text-xl text-[#4A6FA5]">We're here when life happens.</p>
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/care-portal/request-support"
              data-testid="start-here-request-support-btn"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm sm:text-base font-semibold shadow-sm transition"
            >
              <MessageSquare className="w-4 h-4" strokeWidth={2} aria-hidden="true" /> Request Confidential Support
            </Link>
            <a
              href={scheduleTarget}
              target={scheduleExternal ? "_blank" : undefined}
              rel={scheduleExternal ? "noopener noreferrer" : undefined}
              data-testid="start-here-schedule-btn"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border-2 border-[#4A6FA5] text-[#4A6FA5] hover:bg-[#F0F4F8] text-sm sm:text-base font-semibold transition"
            >
              <Calendar className="w-4 h-4" strokeWidth={2} aria-hidden="true" /> Schedule a Chaplain
            </a>
          </div>
          <p className="mt-6 text-xs text-slate-500">
            If this is a life-threatening emergency, please call 911 (or your local emergency number) or a mental-health crisis line.
          </p>
        </div>
      </div>
    </section>
    </div>
  );
}
